import useTranslation from 'next-translate/useTranslation';
import { ComponentProps } from '@components/strapi/DynamicZoneComponent';
import { MapSectionDTO } from '@lib/strapi/dtos/components/sections/mapSection.dto';
import StaticMap from '@components/utils/static-map';
import { Prose, SectionHeading } from '../shared-ui';
import { FooterProps } from '@lib/models/layout/footer';
import { directionsUrl, fullAddress } from '@lib/utils/geo';
import { IconPin, IconClock } from '@components/utils/icons';
import * as gtag from '@lib/utilities/analytics';

/**
 * «Πού θα μας βρείτε» — a LOCAL static image linking out to Google Maps (D48).
 * Address falls back to site-settings via the page's footer props, so the
 * section needs no configuration for the clinic's single address.
 *
 * Restructured (owner, 2026-09-10) from the map+side-column grid to a
 * vertical composition in the standard section grammar:
 *   SectionHeading (pin icon) + the invite as its serif lede
 *   → address · hours strip (answers where/when before the map is even read)
 *   → the map FULL-WIDTH (it earns the space once the column is gone)
 *   → transit directions (editor-owned richtext — the static map cannot pan,
 *     so the metro/bus lines are what make it usable)
 *   → one BIG centered CTA: directions from the visitor's current location
 *     (Google's `dir` API with no origin uses the device location; a chosen
 *     click, so D48 holds).
 */
const MapSectionImplementation = ({ section, page, index }: ComponentProps<MapSectionDTO>) => {
  const { t } = useTranslation('main');
  const footer = (page as { footer?: FooterProps } | undefined)?.footer;
  if (!footer?.googleMapsUrl) return null;
  const address = fullAddress(footer);

  return (
    <section className="my-14 md:my-20">
      <SectionHeading heading={t('map.heading')} icon={<IconPin />}
        introText={section.invite || t('map.invite')} />

      {/* where/when at a glance — the address links to directions-from-here,
          same as the footer (owner, 2026-09-07: no dead address text) */}
      <div className="mb-5 flex flex-wrap items-center gap-x-8 gap-y-2">
        {footer.addressStreet ? (
          <a
            href={directionsUrl(address)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => gtag.event('map_directions_click', { event_category: 'engagement', event_label: 'map-address' })}
            className="inline-flex items-center gap-2 hover:underline"
          >
            <IconPin width={17} height={17} className="flex-none text-violet-700" aria-hidden />
            <span className="font-extrabold text-ink">
              {footer.addressStreet},{' '}
              <span className="font-bold text-muted">
                {footer.addressLocality}{footer.addressPostalCode ? ` ${footer.addressPostalCode}` : ''}
              </span>
            </span>
          </a>
        ) : null}
        {footer.workingHours ? (
          <p className="inline-flex items-center gap-2 text-muted">
            <IconClock width={16} height={16} className="flex-none text-violet-700" aria-hidden />
            {footer.workingHours}
          </p>
        ) : null}
      </div>

      <StaticMap
        addressLine={footer.addressStreet ?? ''}
        locality={footer.addressLocality ?? ''}
        postalCode={footer.addressPostalCode}
        googleMapsUrl={footer.googleMapsUrl}
        openLabel={t('contact.openInMaps')}
        wide
        // in the first couple of zone slots the map IS the likely LCP (the
        // contact page leads with it) — lazy-loading it there was costing
        // seconds; further down the page, lazy stays right
        // never priority (2026-09-16): zone index ≤2 over-triggered — the map
        // sits below the form/FAQ content on every page that embeds it, so it
        // is never the LCP; eager-loading it just competed with real LCP work
        priority={false}
      />

      {section?.showDirections && section?.directions ? (
        <Prose content={section.directions}
          className="mt-6 [&_h3]:mt-4 [&_h3]:mb-1 [&_h3]:text-base [&_h3]:font-extrabold [&_ul]:mb-1" />
      ) : null}

      {address ? (
        <div className="mt-8 text-center">
          <a
            href={directionsUrl(address)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => gtag.event('map_directions_click', { event_category: 'engagement', event_label: 'map-section' })}
            className="inline-flex items-center gap-2.5 rounded-full bg-violet-700 px-8 py-3.5 text-lg font-extrabold text-white transition-colors hover:bg-violet-800"
          >
            <IconPin width={19} height={19} aria-hidden />
            {t('map.directionsCta')}
          </a>
        </div>
      ) : null}
    </section>
  );
};

export default MapSectionImplementation;
